import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminPressMedia() {
  const [pressMedia, setPressMedia] = useState([]);
  const [title, setTitle] = useState('');
  const [media, setMedia] = useState('');
  const [link, setLink] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPressMedia();
  }, []);

  const fetchPressMedia = async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in to manage press & media.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/press-media`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setPressMedia(response.data.pressMedia);
      } else {
        setError(response.data.message || 'Failed to fetch press & media.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching press & media.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setTitle('');
    setMedia('');
    setLink('');
    setImage(null);
    setImagePreview(null);
    setShowForm(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('media', media);
    formData.append('link', link);
    if (image) formData.append('image', image);

    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('Authentication required.');
        setSaving(false);
        return;
      }
      const response = await axios.post(`${apiUrl}/api/admin/press-media`, formData, {
        headers: {
          Authorization: `Bearer ${sessionData.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success) {
        setSuccess('Press & media item added successfully.');
        resetForm();
        fetchPressMedia();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to add press & media item.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding press & media item.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this press & media item?')) {
      try {
        const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
        const response = await axios.delete(`${apiUrl}/api/admin/press-media/${id}`, {
          headers: { Authorization: `Bearer ${sessionData.token}` }
        });
        if (response.data.success) {
          fetchPressMedia();
        } else {
          setError(response.data.message || 'Failed to delete press & media item.');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting press & media item.');
      }
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link
                    to="/admin-dashboard"
                    className="text-gray-200 fw-normal text-15 hover-text-main-600"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Press & Media</span>
                </li>
              </ul>
            </div>
            <button
              type="button"
              className="btn btn-main rounded-pill py-9"
              onClick={() => { showForm ? resetForm() : setShowForm(true); }}
            >
              <i className={`ph ${showForm ? 'ph-x' : 'ph-plus'} me-2`} /> {showForm ? 'Cancel' : 'Add Press & Media Item'}
            </button>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {showForm && (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100">
                <h5 className="mb-0">Add New Press & Media Item</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-20">
                    <div className="col-12">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">
                        Image <span className="text-red-500">*</span>
                      </label>
                      <div className="d-flex align-items-center gap-16">
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="preview"
                            style={{ width: 100, height: 80, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
                          />
                        ) : (
                          <div style={{
                            width: 100, height: 80, borderRadius: 12, background: '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '2px dashed #d1d5db'
                          }}>
                            <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control py-11"
                          accept="image/*"
                          onChange={handleImageChange}
                          required
                          style={{ maxWidth: 300 }}
                        />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <label htmlFor="title" className="h5 mb-8 fw-semibold font-heading">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="title"
                        className="form-control py-11"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={saving}
                        required
                      />
                    </div>
                    <div className="col-sm-6">
                      <label htmlFor="media" className="h5 mb-8 fw-semibold font-heading">
                        Media / Publication Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="media"
                        className="form-control py-11"
                        placeholder="e.g. The Arabian Mirror"
                        value={media}
                        onChange={(e) => setMedia(e.target.value)}
                        disabled={saving}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="link" className="h5 mb-8 fw-semibold font-heading">
                        Coverage Link
                      </label>
                      <input
                        type="url"
                        id="link"
                        className="form-control py-11"
                        placeholder="https://..."
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-12 flex-align justify-content-end gap-8">
                      <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={resetForm}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                        {saving ? 'Saving...' : 'Add Press & Media Item'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">All Press & Media ({pressMedia.length})</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : pressMedia.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No press & media items yet. Add one above.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">#</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Coverage</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Media</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Link</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pressMedia.map((item, i) => (
                        <tr key={item.id}>
                          <td className="py-12 px-20 text-15">{i + 1}</td>
                          <td className="py-12 px-20" style={{ maxWidth: 360 }}>
                            <div className="d-flex align-items-center gap-12">
                              <img
                                src={item.imagepath ? `${apiUrl}/${item.imagepath}` : 'assets/img/bg/blog.png'}
                                alt={item.title}
                                style={{ width: 64, height: 48, borderRadius: 8, objectFit: 'cover', border: '2px solid #e8f5ee', flexShrink: 0 }}
                              />
                              <p className="text-14 fw-medium text-heading mb-0" style={{
                                overflow: 'hidden', textOverflow: 'ellipsis',
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                              }}>
                                {item.title}
                              </p>
                            </div>
                          </td>
                          <td className="py-12 px-20">
                            <span className="text-14 text-gray-500">{item.media}</span>
                          </td>
                          <td className="py-12 px-20">
                            {item.link ? (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="badge border-0 py-4 px-12 rounded-pill text-13 fw-medium bg-main-50 text-main-600"
                              >
                                <i className="ph ph-arrow-square-out me-1" /> View
                              </a>
                            ) : (
                              <span className="text-13 text-gray-300">N/A</span>
                            )}
                          </td>
                          <td className="py-12 px-20">
                            <div className="d-flex align-items-center gap-8">
                              <Link
                                to={`/admin-edit-press-media/${item.id}`}
                                className="btn btn-sm btn-outline-main rounded-pill py-4 px-12"
                              >
                                <i className="ph ph-pencil me-1" /> Edit
                              </Link>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger rounded-pill py-4 px-12"
                                onClick={() => handleDelete(item.id)}
                              >
                                <i className="ph ph-trash me-1" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminPressMedia;
